/**
 * CUSTOM CODE — Sequential variant selection + gated Add to Cart.
 *
 * Requirements this implements:
 *  1. No option is pre-selected on page load (works with the matching
 *     variant-dropdown.liquid change, and clears any pre-checked
 *     button/radio pickers on its own).
 *  2. Option 2 (and 3, 4, ...) stay locked/uninteractive until the option
 *     before them has a real value chosen.
 *  3. Changing an earlier option clears and re-locks every option after it,
 *     since the valid values for later options can depend on the earlier
 *     choice.
 *  4. Add to Cart stays disabled until every option has a real value.
 *
 * This works for both picker types because Impulse renders both the
 * dropdown wrapper (snippets/variant-dropdown.liquid) and the button/radio
 * wrapper (snippets/variant-button.liquid) with the same
 * `class="variant-input-wrap" data-index="optionN"` markup — so one script
 * handles both without caring which one a given product uses.
 *
 * Where to put it: assets/custom-variant-selection.js
 * How to load it: see README-integration-instructions.md
 */
(function () {
  'use strict';

  var STYLE_ID = 'custom-variant-selection-style';
  var PENDING_LABEL = 'Please make a selection'; // change this text if you want different wording

  function injectStyleOnce() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.variant-input-wrap--locked{opacity:.45;pointer-events:none;}';
    document.head.appendChild(style);
  }

  function getGroups(section) {
    var groups = Array.prototype.slice.call(
      section.querySelectorAll('.variant-input-wrap[data-index]')
    );
    groups.sort(function (a, b) {
      var ai = parseInt(a.getAttribute('data-index').replace('option', ''), 10) || 0;
      var bi = parseInt(b.getAttribute('data-index').replace('option', ''), 10) || 0;
      return ai - bi;
    });
    return groups;
  }

  function getGroupValue(group) {
    var select = group.querySelector('select[data-variant-input]');
    if (select) {
      if (select.value === '') return null;
      var opt = select.options[select.selectedIndex];
      if (opt && opt.hasAttribute('data-placeholder-option')) return null;
      return select.value;
    }
    var checked = group.querySelector('input[type="radio"][data-variant-input]:checked');
    return checked ? checked.value : null;
  }

  function clearGroup(group) {
    var select = group.querySelector('select[data-variant-input]');
    if (select) select.value = '';
    var radios = group.querySelectorAll('input[type="radio"][data-variant-input]');
    radios.forEach(function (r) { r.checked = false; });
  }

  function lockGroup(group, locked) {
    group.classList.toggle('variant-input-wrap--locked', locked);
    var select = group.querySelector('select[data-variant-input]');
    if (select) select.disabled = locked;
    var radios = group.querySelectorAll('input[type="radio"][data-variant-input]');
    radios.forEach(function (r) { r.disabled = locked; });
  }

  function initSection(section) {
    if (!section || section.dataset.customVariantInit === 'true') return;
    var groups = getGroups(section);
    if (!groups.length) return; // no options on this product
    section.dataset.customVariantInit = 'true';

    var form = section.querySelector('form[id^="AddToCartForm"], form.product-form, form[data-product-form]');
    var submitBtn = form
      ? form.querySelector('[type="submit"], button[name="add"], .product-form__cart-submit')
      : section.querySelector('[type="submit"], button[name="add"]');

    // Start clean: clear any pre-checked/pre-filled group.
    groups.forEach(clearGroup);

    function refreshCascade() {
      var allFilledSoFar = true;
      groups.forEach(function (group, idx) {
        if (idx === 0) {
          lockGroup(group, false);
        } else {
          lockGroup(group, !allFilledSoFar);
          if (!allFilledSoFar) clearGroup(group);
        }
        if (getGroupValue(group) === null) allFilledSoFar = false;
      });
      return allFilledSoFar; // true only if every group, in order, has a value
    }

    // Remember the button's original markup (text, icons, spinner elements,
    // whatever the theme puts in there) so it can be restored exactly once
    // a full selection is made — we're not permanently replacing it.
    var originalLabel = submitBtn ? submitBtn.innerHTML : null;
    var showingPendingLabel = false;

    function setSubmitState(complete) {
      if (!submitBtn) return;
      submitBtn.disabled = !complete;
      submitBtn.classList.toggle('disabled', !complete);
      submitBtn.setAttribute('aria-disabled', String(!complete));

      if (!complete && !showingPendingLabel) {
        submitBtn.innerHTML = PENDING_LABEL;
        showingPendingLabel = true;
      } else if (complete && showingPendingLabel) {
        submitBtn.innerHTML = originalLabel;
        showingPendingLabel = false;
      }
    }

    groups.forEach(function (group, idx) {
      var inputs = group.querySelectorAll('select[data-variant-input], input[type="radio"][data-variant-input]');
      inputs.forEach(function (input) {
        input.addEventListener('change', function () {
          // An earlier option changed — wipe everything after it so stale
          // selections can't slip through as "complete".
          for (var j = idx + 1; j < groups.length; j++) clearGroup(groups[j]);
          setSubmitState(refreshCascade());
        });
      });
    });

    // Initial state: lock everything after option 1, disable Add to Cart.
    setSubmitState(refreshCascade());

    // Guard against the theme's own JS re-enabling the button prematurely.
    if (submitBtn) {
      var observer = new MutationObserver(function () {
        var complete = groups.every(function (g) { return getGroupValue(g) !== null; });
        if (!complete && !submitBtn.disabled) {
          submitBtn.disabled = true;
          submitBtn.classList.add('disabled');
          submitBtn.setAttribute('aria-disabled', 'true');
          if (!showingPendingLabel) {
            submitBtn.innerHTML = PENDING_LABEL;
            showingPendingLabel = true;
          }
        }
      });
      observer.observe(submitBtn, { attributes: true, attributeFilter: ['disabled', 'class'] });
    }
  }

  function initAll(root) {
    injectStyleOnce();
    (root || document).querySelectorAll('.product-section').forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });
})();